<template>
  <v-container class="mxw-1200 pt-8">
    <v-row dense>
      <v-col>
        <h1 data-testid="landing-dashboard-title">
          {{ $bct('Welcome') }}<template v-if="firstName">, {{ firstName }}</template>
        </h1>

        <p
          v-if="cards.length"
          class="c-light-black fs-16 mt-1 mb-4"
        >
          {{ $bct(SUBTITLE) }}
        </p>
      </v-col>
    </v-row>

    <v-row v-if="cards.length">
      <v-col
        v-for="card in cards"
        :key="card.testId"
        cols="12"
        md="4"
        sm="6"
      >
        <StandardCard
          :card-test-id="card.testId"
          :divided="false"
          class="h-100pc d-flex flex-column"
        >
          <div class="feature-icon mb-4">
            <v-icon
              :icon="card.icon"
              color="primary"
              size="24"
            />
          </div>

          <h2 class="fs-18 fw-600 mb-2">
            {{ $bct(card.title) }}
          </h2>

          <p class="c-light-black mb-4">
            {{ $bct(card.description) }}
          </p>

          <v-spacer />

          <StandardButton
            :data-testid="`${card.testId}-button`"
            :text="card.buttonText"
            :to="card.route"
            append-icon="east"
            color="primary"
            custom-classes="px-0"
            role="link"
          />
        </StandardCard>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col>
        <StandardCard
          :title="NO_ACCESS_TITLE"
          card-test-id="landing-dashboard-empty"
        >
          <p class="mb-2">
            {{ $bct(NO_ACCESS_MESSAGE) }}
          </p>

          <p v-if="supportEmail">
            <a
              v-text="supportEmail"
              :href="`mailto:${supportEmail}`"
            />
          </p>
        </StandardCard>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { useStore } from 'vuex';

const $bct = inject('translator');

const store = useStore();

const SUBTITLE =
  "You're all set up, but you don't have any location assignments yet. " +
  "Choose where you'd like to go next.";

const NO_ACCESS_TITLE = 'No locations yet';
const NO_ACCESS_MESSAGE =
  'You currently do not have access to any locations, and there are no additional ' +
  'features available for your account. To get access to a location, please contact ' +
  'your administrator or our support team at:';

const REQUEST_ACCESS_DESCRIPTION =
  "You don't have any location assignments yet. Search for a location and request to " +
  'join it to unlock its full set of features.';
const LICENSING_DESCRIPTION =
  'View the licenses associated with your account and create new licenses whenever you need them.';
const LEARNING_DESCRIPTION =
  'Explore the Learning Management System and take self-directed, ad hoc courses at your own pace.';

const firstName = computed(() => store.state.profile?.first_name);

const supportEmail = computed(() => store.state.site?.provider_support_email);

const requestAccessEnabled = computed(() => store.state.config.enable_provider_access_request);

const licensingEnabled = computed(() => store.state.features.enable_provider_licenses);

const learningEnabled = computed(
  () => store.state.pages.Learning?.enabled && store.state.profile?.enable_courses,
);

const cards = computed(() => {
  const result = [];

  if (requestAccessEnabled.value) {
    result.push({
      buttonText: 'Request access',
      description: REQUEST_ACCESS_DESCRIPTION,
      icon: 'add_location_alt',
      route: { name: 'RegisterAccess' },
      testId: 'request-access-card',
      title: 'Request access to a location',
    });
  }

  if (licensingEnabled.value) {
    result.push({
      buttonText: 'Go to licensing',
      description: LICENSING_DESCRIPTION,
      icon: 'task_alt',
      route: { name: 'ProviderLicenseIndex' },
      testId: 'licensing-card',
      title: 'Licensing',
    });
  }

  if (learningEnabled.value) {
    result.push({
      buttonText: 'Start learning',
      description: LEARNING_DESCRIPTION,
      icon: 'school',
      route: { name: 'CourseIndex' },
      testId: 'learning-card',
      title: store.state.pages.Learning?.title || 'Learning',
    });
  }

  return result;
});
</script>

<style scoped>
.feature-icon {
  align-items: center;
  background-color: rgba(var(--v-theme-primary), 0.1);
  border-radius: 8px;
  display: flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}
</style>
